import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getApiHost } from '../../../../app/infrastructure/http/apiClient';
import { unwrapUserProfile } from '../../application/mapUserProfile';
import type { UpdateUserProfileRequest, UserProfile } from '../../application/types';

function authorizationHeader(token?: string | null): string | undefined {
  if (!token) {
    return undefined;
  }
  return token.startsWith('Bearer ') ? token : `Bearer ${token}`;
}

function apiBaseUrl(): string {
  const host = getApiHost();
  return host.endsWith('/') ? host : `${host}/`;
}

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: fetchBaseQuery({
    baseUrl: apiBaseUrl(),
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
      const token = (
        getState() as { authorization?: { accessToken?: string | null } }
      ).authorization?.accessToken;
      const value = authorizationHeader(token);
      if (value) {
        headers.set('Authorization', value);
      }
      return headers;
    },
  }),
  tagTypes: ['UserProfile'],
  endpoints: (build) => ({
    getProfile: build.query<UserProfile, void>({
      query: () => ({
        url: 'users/profile',
        method: 'GET',
      }),
      transformResponse: unwrapUserProfile,
      providesTags: ['UserProfile'],
    }),
    updateProfile: build.mutation<UserProfile, UpdateUserProfileRequest>({
      query: (body) => ({
        url: 'users/profile',
        method: 'PATCH',
        body,
      }),
      transformResponse: unwrapUserProfile,
      invalidatesTags: ['UserProfile'],
    }),
  }),
});

export const { useGetProfileQuery, useUpdateProfileMutation } = userApi;
